import React from 'react';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Tooltip } from '../../components';
import { ATTACHED_DEVICE_KEYS, COLUMN_HINTS, CONTROLLER_ONLY_KEYS, COUNTER_ONLY_KEYS } from './constants';
import {
  formatAttachedOnly,
  formatControllerOnly,
  formatCounterOnly,
  formatDateTime,
  formatNumber,
  formatValue,
  formatVoltage,
  getCompanyName,
  getDeviceTypeKey,
  getDictName,
  getStateLabel,
} from './utils';

const formatStates = (states, row, statesByType) => {
  if (!Array.isArray(states) || !states.length) return '-';

  return (
    <div className="flex flex-col gap-1 min-w-[140px]">
      {states.map((state, index) => {
        const label = getStateLabel(state, getDeviceTypeKey(row), statesByType);
        if (!label) return null;

        return (
          <div key={state.key || index} className="flex items-center gap-x-1">
            {state.is_error ? <ErrorOutlineIcon fontSize="small" className="text-red-500" /> : null}
            <span className={state.is_error ? 'text-red-500' : undefined}>{label}</span>
          </div>
        );
      })}
    </div>
  );
};

export const getDevicesColumns = ({ isMeridianOrg, organizationsById, handleSwitchToUserAccount, typeKey, defs = {} }) => {
  const { subtypesByKey, outputsByKey, statesByType } = defs;

  const allColumns = [
    { key: 'serial_id', title: 'Серийный номер', render: formatValue },
    { key: 'type_name', title: 'Тип уст-ва', render: (value) => value || '-' },
    {
      key: 'subtype_name',
      title: 'Подтип',
      render: formatAttachedOnly((value, row) => formatValue(getDictName(value, row?.subtype_key, subtypesByKey))),
    },
    {
      key: 'output_type_name',
      title: 'Тип выхода',
      render: formatAttachedOnly((value, row) => formatValue(getDictName(value, row?.output_type_key, outputsByKey))),
    },
    { key: 'address', title: 'Адрес', render: formatValue },
    { key: 'description', title: 'Описание', render: formatValue },
    ...(isMeridianOrg
      ? [
          {
            key: 'organization_id',
            title: 'Компания',
            render: (value) => {
              const name = getCompanyName(value, organizationsById);
              if (!name) return '-';

              return (
                <span
                  title={name}
                  className="inline-flex max-w-[220px] truncate items-center rounded-full bg-orange-100 px-2.5 py-1 text-sm font-medium text-orange-800">
                  {name}
                </span>
              );
            },
          },
        ]
      : []),
    { key: 'device_version', title: 'Версия прошивки', render: formatControllerOnly(formatValue) },
    { key: 'voltage', title: 'Напряжение, (В)', render: formatControllerOnly(formatVoltage) },
    { key: 'signal', title: 'Сигнал', render: formatControllerOnly(formatValue) },
    { key: 'reset_count', title: 'Кол-во перезагрузок', render: formatControllerOnly(formatValue) },
    { key: 'prev_esp', title: 'Время связи, (мс)', hint: COLUMN_HINTS.prev_esp, render: formatControllerOnly(formatValue) },
    { key: 'sum_esp', title: 'Время записи, (мс)', hint: COLUMN_HINTS.sum_esp, render: formatControllerOnly(formatValue) },
    {
      key: 'states',
      title: 'Состояния',
      render: (value, row) => formatStates(value, row, statesByType),
    },
    { key: 'error_description', title: 'Описание ошибки', render: formatControllerOnly(formatValue) },
    { key: 'm_multiplier', title: 'Коэффициент расхода воды', render: formatAttachedOnly(formatNumber) },
    { key: 'm_add', title: 'Начальное показание (литры)', render: formatAttachedOnly(formatNumber) },
    { key: 'calc_value', title: 'Показания, (литры)', hint: COLUMN_HINTS.calc_value, render: formatCounterOnly(formatValue) },
    { key: 'raw_value', title: 'Кол-во импульсов', hint: COLUMN_HINTS.raw_value, render: formatCounterOnly(formatValue) },
    { key: 'event_time', title: 'Время события', render: formatDateTime },
    {
      key: 'actions',
      title: 'Действия',
      render: (row) => (
        <div className={'text-center flex items-center justify-center'}>
          <Tooltip text="Перейти в ЛК">
            <button
              onClick={() => handleSwitchToUserAccount(row?.user_id)}
              className="p-1 flex items-center justify-center rounded-full border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition">
              <ArrowForward />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  if (!typeKey) return allColumns;

  return allColumns.filter((col) => {
    if (CONTROLLER_ONLY_KEYS.has(col.key)) return typeKey === 'controller';
    if (COUNTER_ONLY_KEYS.has(col.key)) return typeKey === 'counter';
    if (ATTACHED_DEVICE_KEYS.has(col.key)) return typeKey === 'counter' || typeKey === 'sensor';
    return true;
  });
};
